import React, {memo, useEffect} from 'react';
import {Button, Checkbox, DatePicker, DatePickerProps, Select, Space, Switch} from "antd";
import {RangePickerProps} from "antd/es/date-picker";
import dayjs from "dayjs";
import locale from "antd/es/date-picker/locale/ru_RU";
import {useAppDispatch, useAppSelector} from "../../../redux/store";
import {
    selectCommerceFilters,
    selectCommerceLoadingFilters,
    selectCommerceMediaType,
    selectCommerceRegion,
    selectCommerceSpm,
    selectCommerceTime,
    selectCommerceType,
    selectCommerceOts,
    selectCommerceChartMode
} from "../../../common/selectors";
import {
    fetchFiltersCommerce,
    resetCommerceTimeAC,
    setCommerceTimeAC,
    setCommerceTypeAC,
    setMediaTypeAC,
    setSelectedRegionAC,
    setSelectedSpmAC,
    setOtsAC,
    initSelectedSpmAC,
    setChartModeAC,
    ALL_REGIONS_VALUE
} from "../../../redux/commerceReducer";
import {commerceOptions, commerceOrderData} from "../../../common/optionsForCheckBoxes";
import dateIcon from "../../../assets/datePickerIcon.svg";
import closeIcon from "../../../assets/close.svg";
import styles from './commerceOptions.module.sass';
import {CheckboxValueType} from "antd/es/checkbox/Group";
import {Spinner} from "../../../common/spin";

const itemsMediaType = [
    {label: 'Реклама ТВ', value: 'tv'},
    {label: 'Реклама Интернет', value: 'internet_video'},
    {label: 'Реклама Наружка', value: 'outdoor_advertising'},
]

const itemsCommerceType = [
    {label: 'Маркетплейсы', value: 'ecom'},
    {label: 'Супермаркеты', value: 'egroc'}
]

// Функция для форматирования названия региона (первая буква заглавная, остальные строчные)
const formatRegionLabel = (value: string): string => {
    // Обрабатываем составные названия через дефис
    const formattedDash = value.split('-').map(part =>
        part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
    ).join('-')

    // Обрабатываем составные названия через пробел (первое слово с большой, остальные с маленькой)
    const words = formattedDash.split(' ')
    if (words.length > 1) {
        return words[0].charAt(0).toUpperCase() + words[0].slice(1).toLowerCase() + ' ' +
            words.slice(1).map(w => w.toLowerCase()).join(' ')
    }
    return formattedDash
}


// Порядок сортировки регионов (в том виде, как приходят с API)
const regionOrder = ['СЕТЕВОЕ ВЕЩАНИЕ', 'МОСКВА', 'САНКТ-ПЕТЕРБУРГ']

export const CommerceOptions = memo(() => {
    const dispatch = useAppDispatch()

    const commerceType = useAppSelector(selectCommerceType)
    //const mediaType = useAppSelector(selectCommerceMediaType)
    const filters = useAppSelector(selectCommerceFilters)
    const loadingFilters = useAppSelector(selectCommerceLoadingFilters)
    const selectedRegion = useAppSelector(selectCommerceRegion)
    const selectedSpm = useAppSelector(selectCommerceSpm)
    const time = useAppSelector(selectCommerceTime)
    const ots = useAppSelector(selectCommerceOts)
    const chartMode = useAppSelector(selectCommerceChartMode)

    const {RangePicker} = DatePicker

    // Функция получения label для spm из commerceOptions
    const getSpmLabel = (value: string): string => {
        const option = commerceOptions.find(opt => opt.value === value)
        return option ? option.label : value
    }

    // Сортировка и форматирование супермаркетов
    const sortedSpm = [...filters.spm].sort((a, b) => {
        const indexA = commerceOrderData.indexOf(a)
        const indexB = commerceOrderData.indexOf(b)
        if (indexA !== -1 && indexB !== -1) return indexA - indexB
        if (indexA !== -1) return -1
        if (indexB !== -1) return 1
        return a.localeCompare(b)
    }).map(spm => ({
        label: getSpmLabel(spm),
        value: spm
    }))

    // Сортировка и форматирование регионов
    const sortedRegions = [
        { label: 'Все', value: ALL_REGIONS_VALUE },
        ...[...filters.regions].sort((a, b) => {
            const indexA = regionOrder.indexOf(a)
            const indexB = regionOrder.indexOf(b)
            if (indexA !== -1 && indexB !== -1) return indexA - indexB
            if (indexA !== -1) return -1
            if (indexB !== -1) return 1
            return a.localeCompare(b)
        }).map(region => ({
            label: formatRegionLabel(region),
            value: region
        }))
    ]

    // Автоматический запрос фильтров при смене commerceType
    useEffect(() => {
        const request = dispatch(fetchFiltersCommerce({com_type: commerceType}))
        return () => {
            request.abort()
        }
    }, [dispatch, commerceType])

    // Инициализация всех супермаркетов при загрузке фильтров (и при смене commerceType)
    useEffect(() => {
        if (filters.spm.length > 0) {
            dispatch(initSelectedSpmAC(filters.spm))
        }
    }, [dispatch, filters.spm])

    // Установка региона "Все" по умолчанию при загрузке фильтров
    useEffect(() => {
        if (filters.regions.length > 0 && selectedRegion.length === 0) {
            dispatch(setSelectedRegionAC([ALL_REGIONS_VALUE]))
        }
    }, [dispatch, filters.regions, selectedRegion])

    // Обработчики для кнопок CommerceType
    const onCommerceTypeChange = (value: 'ecom' | 'egroc') => {
        dispatch(setCommerceTypeAC(value))
    }

    // Обработчик для селекта MediaType
    const onMediaTypeChange = (value: string) => {
        dispatch(setMediaTypeAC(value))
    }

    // Обработчик для чекбоксов регионов (множественный выбор с логикой "Все")
    const onRegionChange = (checkedValues: CheckboxValueType[]) => {
        const values = checkedValues as string[]
        const prevHadAll = selectedRegion.includes(ALL_REGIONS_VALUE)
        const nowHasAll = values.includes(ALL_REGIONS_VALUE)

        if (nowHasAll && !prevHadAll) {
            // Выбрали "Все" - снимаем все остальные
            dispatch(setSelectedRegionAC([ALL_REGIONS_VALUE]))
        } else if (nowHasAll && prevHadAll && values.length > 1) {
            // Было "Все", выбрали ещё что-то - снимаем "Все"
            dispatch(setSelectedRegionAC(values.filter(v => v !== ALL_REGIONS_VALUE)))
        } else if (values.length === 0) {
            // Ничего не выбрано - ставим "Все"
            dispatch(setSelectedRegionAC([ALL_REGIONS_VALUE]))
        } else {
            dispatch(setSelectedRegionAC(values))
        }
    }

    // Обработчик для чекбоксов spm
    const onSpmChange = (checkedValues: CheckboxValueType[]) => {
        dispatch(setSelectedSpmAC(checkedValues as string[]))
    }

    // Обработчик для RangePicker
    const onChange = (
        value: DatePickerProps['value'] | RangePickerProps['value'],
        dateString: [string, string] | string,
    ) => {
        if (value && Array.isArray(dateString)) {
            const start = dayjs(dateString[0]).format('YYYY-MM-DD')
            const end = dayjs(dateString[1]).format('YYYY-MM-DD')
            sessionStorage.setItem('startCommerceTime', start)
            sessionStorage.setItem('endCommerceTime', end)
            dispatch(setCommerceTimeAC([start, end]))
        }
    }

    // Сброс к стандартному периоду
    const onResetTime = () => {
        sessionStorage.removeItem('startCommerceTime')
        sessionStorage.removeItem('endCommerceTime')
        dispatch(resetCommerceTimeAC())
    }

    // Обработчик для чекбокса OTS
    const onOtsChange = (checked: boolean) => {
        dispatch(setOtsAC(checked))
    }

    const customFormat = (value: any) => {
        return value.format("YYYY-MM-DD")
    }

    return (
        <div className={styles.main}>
            {/* Кнопки CommerceType */}
            <div className={styles.buttons}>
                {itemsCommerceType.map((item) => (
                    <Button
                        key={item.value}
                        className={styles.button}
                        onClick={() => onCommerceTypeChange(item.value as 'ecom' | 'egroc')}
                        type={commerceType === item.value ? 'default' : 'primary'}
                    >
                        {item.label}
                    </Button>
                ))}
            </div>

            {/* Селект MediaType */}
            {/*<div className={styles.selectWrapper}>
                <Select
                    value={mediaType}
                    onChange={onMediaTypeChange}
                    options={itemsMediaType}
                    style={{width: '100%'}}
                />
            </div>*/}

            {/* Чекбоксы супермаркетов */}
            <div className={styles.checkboxGroup}>
                <div className={styles.checkboxTitle}>Супермаркеты</div>
                {loadingFilters
                    ? <div style={{height: 300}}><Spinner/></div>
                    : <Checkbox.Group
                        options={sortedSpm}
                        value={selectedSpm}
                        onChange={onSpmChange}
                        style={{display: 'flex', flexDirection: 'column', gap: '8px'}}
                    />
                }
            </div>

            {/* Чекбоксы регионов (множественный выбор) */}
            <div className={styles.regionsWrapper}>
                <div className={styles.checkboxTitle}>Регионы</div>
                {loadingFilters
                    ? <div style={{height: 300}}><Spinner/></div>
                    : <div className={styles.regionsScroll}>
                        <Checkbox.Group
                            value={selectedRegion}
                            onChange={onRegionChange}
                            style={{display: 'flex', flexDirection: 'column', gap: '8px'}}
                        >
                            {sortedRegions.map(region => (
                                <Checkbox key={region.value} value={region.value}>
                                    {region.label}
                                </Checkbox>
                            ))}
                        </Checkbox.Group>
                    </div>
                }
            </div>

            {/* Период */}
            <div className={styles.periodBlock}>
                <div className={styles.title}>
                    Период
                    <Button type={'link'} onClick={onResetTime}>Стандартный период</Button>
                </div>
                <Space direction="vertical" size={12}>
                    <RangePicker
                        value={[dayjs(time[0]), dayjs(time[1])]}
                        style={{width: '320px'}}
                        picker={'week'}
                        locale={locale}
                        suffixIcon={<img src={dateIcon} alt={'select'}/>}
                        allowClear={{
                            clearIcon: <img className={styles.closeIcon} src={closeIcon} alt={'clear'}/>
                        }}
                        format={customFormat}
                        onChange={onChange}
                    />
                </Space>
            </div>

            {/* Свитч типа графика */}
            <div className={styles.switchBlock}>
                <Switch
                    checked={chartMode === 'bar'}
                    onChange={(checked) => dispatch(setChartModeAC(checked ? 'bar' : 'line'))}
                />
                <span className={styles.switchLabel}>Сплит</span>
            </div>
        </div>
    );
});

export default CommerceOptions;
